import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../ReusableComponents/supabaseClient";
import { IoChevronBackOutline } from "react-icons/io5";
import StatusBadge from "../ReusableComponents/StatusBadge";
import moment from "moment";
import "moment-timezone";
import { logError } from "../Utilities/errorLogger";
import { useAuth } from "../Utilities/AuthContext";

const AllLeaderboard = () => {
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { ownerData } = useAuth();
  const loggedInUser = Number(ownerData?.userId || localStorage.getItem("userId"));

  useEffect(() => {
    const fetchPools = async () => {
      try {
        setLoading(true);

        const { data: ownerPools, error: ownerError } = await supabase
          .from("pool")
          .select(`id, name, current_round, round_status, tournament_status, tournament_fixture ( id, name, start_date )`)
          .eq("owner", loggedInUser);

        if (ownerError) throw ownerError;

        const { data: invitedPools, error: inviteError } = await supabase
          .from("InviteLink")
          .select(`pool ( id, name, current_round, round_status, tournament_status, tournament_fixture ( id, name, start_date ) )`)
          .eq("person", loggedInUser);

        if (inviteError) throw inviteError;

        const allPools = [...(ownerPools || []), ...(invitedPools?.map((ip) => ip.pool) || [])];
        const uniquePools = Array.from(new Map(allPools.map((pool) => [pool.id, pool])).values()).sort((a, b) => a.id - b.id);

        setPools(uniquePools);
      } catch (error) {
        await logError({
          filePath: "Leaderboard/AllLeaderboards.js",
          functionName: "fetchPools",
          errorType: "Pools Fetch Error",
          errorMessage: error.message,
          stackTrace: error.stack,
          requestData: { loggedInUser },
        });
      } finally {
        setLoading(false);
      }
    };

    if (loggedInUser) fetchPools();
    else setLoading(false);
  }, [loggedInUser]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 md:pt-20">
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-white rounded-xl p-4 flex justify-between">
              <div className="space-y-2 flex-1"><div className="h-4 bg-gray-200 rounded w-2/3" /><div className="h-3 bg-gray-100 rounded w-1/3" /></div>
              <div className="h-6 w-20 bg-gray-200 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 md:pt-20 mb-24 md:mb-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-primary-light rounded-2xl p-6 mb-6 shadow-lg">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-green-200 hover:text-white transition-colors">
            <IoChevronBackOutline className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-white tracking-tight">Leaderboards</h1>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      {pools.length === 0 ? (
        <div className="p-12 text-center">
          <p className="text-gray-400 text-lg">No leaderboards yet</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          {pools.map((pool) => (
            <div
              key={pool.id}
              onClick={() => navigate(`/leaderboard/${pool.id}`)}
              className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-all cursor-pointer"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {pool.name || pool.tournament_fixture?.name}
                </p>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                  {pool.tournament_fixture?.name && pool.tournament_fixture.name !== pool.name && (
                    <span>{pool.tournament_fixture.name}</span>
                  )}
                  <span>
                    {pool.tournament_fixture?.start_date
                      ? moment(pool.tournament_fixture.start_date).format("MMM D, YYYY")
                      : "No date"}
                  </span>
                  {pool.current_round && <span>Round {pool.current_round}</span>}
                  {pool.round_status && pool.round_status !== "Not Started" && (
                    <StatusBadge status={pool.round_status} />
                  )}
                </div>
              </div>
              <StatusBadge status={pool.tournament_status || "Not Started"} />
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

export default AllLeaderboard;
